// ============================================================
// buildMessage (v8.363.1): a parsed line becomes the document that goes
// on the wire. The id is minted by an injected `mint` (utils/entropy in
// the app, a counter in tests - never Math.random), the roll is rolled
// by the caller and handed in. Pure.
// ============================================================
import type { ParsedLine } from './parse';
import { audienceFor, convOf, targetOf, type Target } from './audience';
import { extractMentions } from './mentions';
import type { ChatChannel, ChatMessage, ConversationKey, Person, RollPayload, SpeakAs } from './types';
import { MAX_TEXT } from './types';

export interface BuildContext {
  me: { uid: string; seat: string; name: string; isGm: boolean };
  gmUid: string | null;
  speakAs: SpeakAs | null;
  channels: ChatChannel[];
  people: Person[];
  /** Where the composer is aimed when the line names no target. */
  activeConv: ConversationKey;
  now: number;
  mint: () => string;
}

/** The target a parsed line resolves to, given where the composer is aimed. */
function targetFor(parsed: ParsedLine, activeConv: ConversationKey): Target | null {
  switch (parsed.kind) {
    case 'whisper':
      return { kind: 'dm', uid: parsed.uid };
    case 'group':
      return parsed.channelId === 'players' ? { kind: 'players' } : { kind: 'channel', id: parsed.channelId };
    case 'announce':
      // An announcement is to the table, wherever the composer is aimed.
      return targetOf('table');
    case 'say':
    case 'emote':
    case 'roll':
      return targetOf(activeConv);
    default:
      return null;
  }
}

/** The vox kind a parsed line is written as; anything else is plain speech. */
const KIND_OF: Partial<Record<ParsedLine['kind'], ChatMessage['kind']>> = {
  emote: 'emote',
  roll: 'roll',
  announce: 'system',
};

export function buildMessage(
  parsed: ParsedLine,
  ctx: BuildContext,
  roll: RollPayload | null = null,
): ChatMessage | null {
  const target = targetFor(parsed, ctx.activeConv);
  if (!target) return null;
  const text =
    parsed.kind === 'roll'
      ? parsed.expr
      : parsed.kind === 'ic' || parsed.kind === 'ooc' || parsed.kind === 'error'
        ? ''
        : parsed.text;
  const kind = KIND_OF[parsed.kind] ?? 'say';
  return {
    id: ctx.mint(),
    ts: ctx.now,
    fromUid: ctx.me.uid,
    fromSeat: ctx.me.seat,
    fromName: ctx.me.name,
    // The GM announces as the GM, never in a character's voice.
    speakAs: kind === 'system' ? null : ctx.speakAs,
    kind,
    text: text.slice(0, MAX_TEXT),
    conv: convOf(target),
    audience: audienceFor(target, ctx.me.uid, ctx.channels, ctx.gmUid),
    mentions: extractMentions(text, ctx.people),
    roll,
    handoutId: '',
  };
}
